import {
  useEffect, useMemo, useState
} from 'react';
import useSWR from 'swr';

import get from 'lodash/get';

import {
  fillInMissingPoints, filterExcessZeroes, transformResultsIntoValues
} from '../chart_utils/chart_utils';
import { getPollIntervalByStep } from '../../components/contexts/TimeRangeContext/TimeRangeContext';

import { shouldDisablePrometheus, sinatraOrigin } from '../../config';
import keycloakService from '../../keycloak_service';

export const fetchPrometheusQuery = (queryParams) => {
  const [query, startTimestamp, endTimestamp, step] = queryParams;

  if (shouldDisablePrometheus) {
    throw new Error('Prometheus is disabled.');
  }

  if (!query) {
    throw new Error('No query provided.');
  }

  return fetch(
    `${sinatraOrigin}/v1/metrics/api/v1/query_range?query=${encodeURIComponent(
      query,
    )}&start=${startTimestamp}&end=${endTimestamp}&step=${step}`,
    { headers: { Authorization: keycloakService.getAuthHeader() } }
  )
    .then(response => response.json())
    .then((response) => {
      if (response && response.status === 'success') {
        const resultSets = get(response, 'data.result', []);
        const values = transformResultsIntoValues(resultSets);

        if (!values) {
          throw new Error("Couldn't load Prometheus data");
        }

        const normalizedResponse = filterExcessZeroes(fillInMissingPoints(values, startTimestamp, endTimestamp));
        return { values, normalizedResponse };
      }

      throw new Error("Couldn't load Prometheus data")
    })
}

const usePrometheusQuery = (query, {
  startTimestamp: unalignedStartTime,
  endTimestamp,
  step,
  queryKey = query,
  transformData = data => data,
  useRawResponse = false,
  disablePolling = false,
}) => {
  const [isDataLoading, setDataLoading] = useState(false);
  const [currentData, setCurrentData] = useState([]);

  const startTimestamp = unalignedStartTime - (unalignedStartTime % step);
  const refreshInterval = disablePolling ? 0 : getPollIntervalByStep(step);

  const queryParams = [query, startTimestamp, endTimestamp, step];
  const swrValidator = () => !query || isDataLoading ? null : `${queryKey}`;

  const {
    data,
    error,
    isLoading,
    mutate,
  } = useSWR(swrValidator, () => fetchPrometheusQuery(queryParams), {
    refreshInterval,
    focusThrottleInterval: refreshInterval,
    loadingTimeout: 1000,
    refreshWhenHidden: false,
    refreshWhenOffline: false,
    onLoadingSlow: () => setDataLoading(true),
    onSuccess: () => setDataLoading(false),
    onError: () => setDataLoading(false),
    onDiscarded: () => setDataLoading(false),
  });

  const transformedData = useMemo(() => transformData(currentData), [currentData, transformData]);

  useEffect(() => {
    if (data && data?.values) {
      setDataLoading(false);
      setCurrentData(useRawResponse ? data.values : data.normalizedResponse);
    }
  }, [data, useRawResponse])

  useEffect(() => { mutate() }, [mutate, step]);

  useEffect(() => {
    if (isLoading !== isDataLoading && !isLoading) {
      setDataLoading(false);
    }
  }, [isLoading, isDataLoading])

  return {
    data: transformedData,
    error: error?.message,
    isLoading,
  }
}

export default usePrometheusQuery;
